import { useEffect, useState } from "react";
import { motion } from "framer-motion";

// ScrollProgress: A thin progress bar at the top that indicates scroll position
const ScrollProgress = () => {
  const [progress, setProgress] = useState(0);
  useEffect(() => {
    const handler = () => {
      const total = document.body.scrollHeight - window.innerHeight;
      setProgress((window.scrollY / total) * 100);
    };
    window.addEventListener("scroll", handler);
    return () => window.removeEventListener("scroll", handler);
  }, []);
  return (
    <div className="fixed top-0 left-0 w-full h-1 bg-blue-200 z-50">
      <div
        className="h-full bg-blue-600 transition-all duration-200"
        style={{ width: `${progress}%` }}
      />
    </div>
  );
};

// SectionCard: Animated card wrapper for each section (h3 + content)
const SectionCard = ({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    whileInView={{ opacity: 1, y: 0 }}
    whileHover={{ scale: 1.03, y: -5 }}
    transition={{
      duration: 0.4,
      ease: "easeOut",
      type: "spring",
      stiffness: 200,
      damping: 20,
    }}
    viewport={{ once: true, amount: 0.2 }}
    className="p-6 bg-white/80 backdrop-blur-sm shadow-lg border border-[var(--color-secondary-muted)] rounded"
  >
    <h3 className="text-xl font-bold text-blue-900 mb-3">{title}</h3>
    {children}
  </motion.div>
);

// Paragraph: Styled paragraph component for consistent text appearance
const Paragraph = ({
  children,
  className = "",
}: {
  children: React.ReactNode;
  className?: string;
}) => (
  <p
    className={`text-base text-gray-800 leading-relaxed text-justify ${className}`}
  >
    {children}
  </p>
);

const MarketingEventsAndSponsorshipStrategy: React.FC = () => (
  <div className="relative overflow-hidden space-y-8 px-4 py-10 md:py-6 md:px-8">
    <ScrollProgress />

    {/* Introduction */}
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{
        duration: 0.5,
        ease: "easeOut",
        type: "spring",
        stiffness: 200,
        damping: 20,
      }}
      viewport={{ once: true }}
      className="max-w-6xl mx-auto text-center space-y-4"
    >
      <h2 className="text-3xl font-bold text-primary-dark">
        Marketing, Events, and Sponsorship Strategy in UcWaves
      </h2>
      <Paragraph className="text-gray-700 max-w-6xl mx-auto">
        To drive adoption, foster engagement, and elevate UcWaves’ profile, we
        allocate a portion of the NAU reserve to marketing campaigns, community
        events, and strategic sponsorships. All agreements are codified on-chain
        through dedicated supplier contracts, each with tailored vesting
        schedules and transparent terms. This approach guarantees
        accountability, auditability, and direct community visibility into
        promotional spends.
      </Paragraph>
    </motion.div>

    {/* Two-column layout */}
    <div className="flex flex-col md:flex-row gap-6 max-w-6xl mx-auto">
      <div className="flex-1 flex flex-col space-y-6">
        {/* Section 1 */}
        <SectionCard title="1. Reserve Funding for Marketing and Events">
          <ul className="list-disc list-inside text-base text-gray-800">
            <li>
              <strong>Source:</strong> NAU tokens held in the Project Reserve
              Wallet (200 M NAU).
            </li>
            <br />
            <li>
              <strong>Purpose:</strong> Cover costs of advertising, influencer
              campaigns, community meetups, hackathons, branded merchandise, and
              partnership sponsorships.
            </li>
            <br />
            <li>
              <strong>Budget Management:</strong> Reserve allocations are
              governed by on-chain proposals, with community voting determining
              annual or quarterly spend limits.
            </li>
          </ul>
        </SectionCard>

        {/* Section 2 */}
        <SectionCard title="2. Supplier-Specific Smart Contracts">
          <ul className="list-disc list-inside text-base text-gray-800">
            <li>
              <strong>Contract Per Supplier:</strong> Isolation of liability
              through a unique MarketingSupplier contract per agency or
              organizer, specifying deliverables, total NAU fees, and vesting
              schedule. Immutable once deployed.
            </li>
            <br />
            <li>
              <strong>Vesting Schedules:</strong>

              <ul className="list-disc list-inside ml-5 text-base text-gray-800">
                <li>
                  <strong>MilesNAUNe-Based:</strong> Tokens unlock when specific
                  KPIs (e.g., ad impressions, event attendance) are met.
                </li>
                <br />
                <li>
                  <strong>Time-Based:</strong> Linear vesting over the event
                  period (e.g., 3 months for a conference series).
                </li>
                <br />
                <li>
                  <strong>Security and Commitment:</strong> Vesting aligns
                  supplier incentives with performance and reduces risk of
                  upfront token dumps.
                </li>
              </ul>
            </li>
          </ul>
        </SectionCard>

        {/* Section 3 */}
        <SectionCard title="3. Transparency and Auditing">
          <ul className="list-disc list-inside text-base text-gray-800">
            <li>
              <strong>On-Chain Records:</strong> All supplier contracts, vesting
              states, and token distributions are publicly visible on Base L2.
            </li>
            <br />
            <li>
              <strong>Community Dashboard:</strong> UI displays active marketing
              contracts, vesting progress, and outcomes, empowering community
              verification in real time.
            </li>
          </ul>
        </SectionCard>
      </div>

      <div className="flex-1 flex flex-col space-y-6">
        {/* Section 4 */}
        <SectionCard title="4. Community Involvement and Oversight">
          <ul className="list-disc list-inside text-base text-gray-800">
            <li>
              <strong>Proposal Mechanism:</strong> Community members submit and
              vote on marketing initiatives via governance proposals;
              high-profile campaigns require majority approval.
            </li>
            <br />
            <li>
              <strong>Feedback Loops:</strong> Post-event reports published
              on-chain; token vesting proceeds only after community sign-off on
              delivered results.
            </li>
          </ul>
        </SectionCard>

        {/* Section 5 */}
        <SectionCard title="5. Sponsorship and Partnership Alignment">
          <ul className="list-disc list-inside text-base text-gray-800">
            <li>
              <strong>Strategic Partnerships:</strong> Managed through bespoke
              smart contracts, including co-marketing deliverables and
              reciprocal token allocations.
            </li>
            <br />
            <li>
              <strong>Ecosystem Growth:</strong> Sponsoring hackathons and grant
              programs to cultivate developer contributions and expand feature
              integrations.
            </li>
          </ul>
        </SectionCard>
      </div>
    </div>

    {/* Conclusion */}
    <Paragraph className="text-gray-700 max-w-6xl mx-auto">
      By channeling marketing and sponsorship expenditures through transparent,
      immutable smart contracts and embedding vesting tied to deliverables,
      UcWaves ensures that promotional efforts remain accountable and
      community-driven. This strategy not only safeguards token spend but also
      actively involves the community in shaping the platform’s public presence
      and growth trajectory.
    </Paragraph>
  </div>
);

export default MarketingEventsAndSponsorshipStrategy;
